import React, { useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { X, Phone, Mail, Loader2, Save, Pencil } from 'lucide-react';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <div>
    <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
           className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30"
           data-testid={testId} />
  </div>
);

export const VendorSheet = ({ vendor, onClose, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: vendor.name || '', contact_name: vendor.contact_name || '', email: vendor.email || '',
    phone: vendor.phone || '', description: vendor.description || '',
  });
  const [active, setActive] = useState(!!vendor.active);
  const setF = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/api/admin/vendors/${vendor.vendor_id}`, { ...form, active });
      toast.success('Saved');
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    setBusy(false);
  };

  const toggleActive = async () => {
    const next = !active;
    setActive(next);
    try {
      await api.put(`/api/admin/vendors/${vendor.vendor_id}`, { active: next });
      toast.success(next ? 'Vendor activated' : 'Vendor deactivated');
      onSaved && onSaved();
    } catch (e) { setActive(!next); toast.error('Failed'); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="vendor-sheet">
      <motion.div
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="w-full max-w-lg bg-[#0C0C0C] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div className="min-w-0">
            <p className="text-emerald-400 text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={FONT_B}>Vendor</p>
            <h3 className="text-white text-2xl font-black tracking-tighter truncate" style={FONT_H} data-testid="vendor-sheet-name">{vendor.name}</h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!editing && (
              <button onClick={() => setEditing(true)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-400 active:scale-95 transition-transform" data-testid="vendor-sheet-edit" aria-label="Edit">
                <Pencil className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-300 active:scale-95 transition-transform" data-testid="vendor-sheet-close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!editing ? (
          <div className="space-y-6">
            <div className="flex items-center gap-2.5">
              {vendor.phone && (
                <a href={`tel:${vendor.phone}`} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs font-bold active:scale-95 transition-transform" style={FONT_B}>
                  <Phone className="w-3.5 h-3.5" /> {vendor.phone}
                </a>
              )}
              {vendor.email && (
                <a href={`mailto:${vendor.email}`} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs font-bold active:scale-95 transition-transform truncate" style={FONT_B}>
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{vendor.email}</span>
                </a>
              )}
            </div>
            {vendor.contact_name && <p className="text-zinc-500 text-sm" style={FONT_B}>Contact: <span className="text-zinc-300">{vendor.contact_name}</span></p>}
            {vendor.description && <p className="text-zinc-400 text-sm leading-relaxed" style={FONT_B}>{vendor.description}</p>}

            <div className="flex items-center justify-between bg-[#111111] border border-white/5 rounded-2xl px-5 py-4">
              <div>
                <p className="text-white text-sm font-bold" style={FONT_B}>Active</p>
                <p className="text-zinc-600 text-xs" style={FONT_B}>Inactive vendors don't get new requests</p>
              </div>
              <button onClick={toggleActive}
                      className={`relative w-[52px] h-8 rounded-full transition-colors flex-shrink-0 ${active ? 'bg-emerald-400' : 'bg-white/10'}`}
                      data-testid="vendor-active-toggle" aria-label="Toggle active">
                <span className={`absolute top-1 w-6 h-6 rounded-full transition-transform ${active ? 'bg-black translate-x-[22px]' : 'bg-zinc-500 translate-x-1'}`} />
              </button>
            </div>

            {(vendor.products || []).length > 0 && (
              <div>
                <p className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold mb-3" style={FONT_B}>Animals & Rates</p>
                <div className="space-y-1.5">
                  {vendor.products.map((prod, i) => {
                    const name = typeof prod === 'string' ? prod : (prod.name || prod.id);
                    const rate = typeof prod === 'object' ? prod.baseRate : null;
                    return (
                      <div key={`${name}-${i}`} className="flex items-center justify-between bg-white/[0.02] px-4 py-2.5 rounded-xl">
                        <span className="text-zinc-300 text-[13px]" style={FONT_B}>{name}</span>
                        {rate != null && <span className="text-zinc-500 text-xs tabular-nums font-bold" style={FONT_B}>${rate}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="vendor-edit-form">
            <Field label="Business Name" value={form.name} onChange={setF('name')} testId="vendor-edit-name" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Contact Person" value={form.contact_name} onChange={setF('contact_name')} testId="vendor-edit-contact" />
              <Field label="Phone" value={form.phone} onChange={setF('phone')} type="tel" testId="vendor-edit-phone" />
            </div>
            <Field label="Email" value={form.email} onChange={setF('email')} type="email" testId="vendor-edit-email" />
            <div>
              <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>Description</span>
              <textarea value={form.description} onChange={(e) => setF('description')(e.target.value)} rows={3}
                        className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30 resize-none" data-testid="vendor-edit-description" />
            </div>
            <div className="flex gap-2.5 pt-1">
              <button onClick={save} disabled={busy}
                      className="flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="vendor-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
              <button onClick={() => setEditing(false)} className="px-6 py-3 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="vendor-cancel-edit">
                Cancel
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};
